// "Find your day", both halves of it.
//
// A rating is only half an answer while it is just a colour on a map. This
// module closes the loop:
//
//   authoring  — name a day that was right (a place, a time of year, a time of
//                day — or a holiday photo that says where and when) and its
//                conditions are restated as the recipe: recipeFromConditions in
//                recipe.ts. The landing's "I loved Split in late June, around
//                midday" sentence and the editor's seeder row are two views of
//                ONE draft day, kept here, so whichever a reader touched last is
//                what the other shows.
//   answering  — the "Your day" panel beside the map lists where the day is
//                most like itself, the best place in each world region
//                (/api/best?perRegion=), and when. It refreshes on every edit,
//                so the list is always the field the map is painting, read back
//                as places and dates.
//
// Applying a day lands on the MAP with the panel open — not on the seed city's
// briefing. The question a reader asked was "where else is like this", and the
// map painting the answer beside the list is that answer; the seed's own
// briefing is one row away.
//
// Ephemeral UI state lives here; anything the map owns stays in state.ts and
// the app's mutators arrive through initMyDay's context, so this module never
// imports app.ts.

import { VAR } from '../lib/variables.ts';
import type { VarKey } from '../lib/variables.ts';
import { sectionLabel, sectionLabelShort, PART_LABELS, MONTH_NAMES } from '../lib/sections.ts';
import { track } from './analytics.ts';
import { recipeFromConditions } from '../lib/recipe.ts';
import type { Conditions, Recipe } from '../lib/recipe.ts';
import { parseExif, EXIF_SCAN_BYTES } from '../lib/exif.ts';
import type { ExifResult } from '../lib/exif.ts';
import { mdFor } from '../lib/dekads.ts';
import { dayPath } from '../lib/slug.ts';
import { REGION_LABEL } from '../lib/resources/regions.ts';
import type { RegionKey } from '../lib/resources/regions.ts';
import { listNames } from '../lib/compare-view.ts';
import { escapeHtml } from '../lib/city-view.ts';
import { store, state } from './state.ts';
import { visitorLocation } from './locale.ts';
import type { DaySeed, ComparePlace } from './state.ts';
import { lastPlace } from './last-place.ts';
import type { CityHit } from '../contracts/city.ts';

// The API shapes live in src/contracts, declared once and compiled by both the
// server and the client.
export type { CityRow } from '../contracts/match.ts';
import type { CityRow, NearResponse, ConditionsResponse, Peak, BestResponse } from '../contracts/match.ts';

// The little a day's PLACE has to be: enough to name it in a sentence and to ask
// for its readings. A full city row, a search hit and a saved day all satisfy it.
export interface DayPlace {
  id: number;
  name: string;
  country: string;
  placeSlug?: string;
}

// What openCity is told about a place before its briefing lands.
interface CityHint {
  name: string;
  nameAscii: string;
  country: string;
  pop: number;
  lon: number;
  lat: number;
  placeSlug?: string;
}

// A day the user has authored from, as localStorage keeps it.
interface SavedDay {
  id: number;
  name: string;
  country: string;
  dekad: string;
  bucket: string;
  placeSlug?: string;
  /** When it was authored, ms. Absent on days saved before this was kept. */
  at?: number;
}

// The hooks app.ts hands over, so neither module imports the other. Declared as
// methods rather than function-typed properties: parameters are then checked
// bivariantly, so app.ts's own signatures need only line up in one direction.
export interface MyDayContext {
  openCity(id: number, hint: CityHint): void;
  showFrame(idx: number): void;
  pause(): void;
  wireAutocomplete(
    input: HTMLInputElement,
    box: HTMLElement,
    onPick: (s: CityHit) => void,
    opts?: { clearOnPick?: boolean },
  ): void;
  unlockCustom(): void;
  persist(): void;
  // The time of day the recipe was read at. A day authored at midday is a
  // recipe about middays, so the map has to be looking at the same hour.
  setBucket(bk: string): void;
  applyVariable(v: string): void;
  setRecipe(r: Recipe): void;
  syncCustomEditor(): void;
  // Arrive on the map, and put the camera where the answer reads.
  showMap(center: [number, number], zoom: number, animate: boolean): void;
  // The bridge into Compare: open one now, or add/remove a place from the one
  // being gathered in the tray.
  openCompare(places: ComparePlace[]): void;
  toggleCompare(place: ComparePlace): void;
}

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
// Days the user has authored from — their own, not the map's, so it gets its
// own key beside fcc.map.v1.
const DAYS_KEY = 'fcc.days.v1';
const MAX_SAVED = 6;
const DEKADS_PER_MONTH = 3; // the calendar's own shape — see lib/sections.ts
const PER_REGION = 6; // fetched per region; three show, "more" reveals the rest
const SHOWN_PER_REGION = 3;
const PEAK_DEBOUNCE_MS = 400; // a slider drag must not fire a scan per pixel
// The world view the panel's regions read against: the whole map, centred on
// the day it came from.
const DAY_ZOOM = 1.6;

let ctx: MyDayContext | null = null;

interface UiState {
  city: DayPlace | null;
  month: number | null;
  part: number | null;
  bucket: string | null;
  photo: { url: string; cityRow: DayPlace | null } | null;
  peakToken: number;
  seedToken: number;
  peaks: Peak[];
  renderedKey: string | null;
  activeId: number | null;
  timer: ReturnType<typeof setTimeout> | null;
  expanded: Set<string>;
  // Listeners on the draft — the landing sentence redraws from this.
  onDraft: (() => void)[];
}

const ui: UiState = {
  // Blank until the reader says, or this browser remembers a day of theirs.
  // A preset day would be someone else's answer put in their mouth.
  city: null,
  month: null,
  part: null,
  bucket: null,
  photo: null,
  peakToken: 0,
  seedToken: 0,
  peaks: [],
  renderedKey: null,
  activeId: null,
  timer: null,
  expanded: new Set(),
  onDraft: [],
};

const mdParam = () => mdFor(ui.month!, ui.part!);
const lower1 = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const bucketWord = (key: string) => lower1(state.manifest?.buckets.find((b) => b.key === key)?.label || key);
const dekadKey = (month: number, part: number) => `${String(month).padStart(2, '0')}-${part}`;

// ── the draft day ─────────────────────────────────────────────────────────────
// Read by the landing sentence (ui/day-sentence.ts); written by it, by the
// seeder's fields, by a saved-day chip and by a photo.

export interface Draft {
  city: DayPlace | null;
  month: number | null;
  part: number | null;
  bucket: string | null;
  dekad: string | null;
}

export function getDraft(): Draft {
  const dekad = ui.month != null && ui.part != null ? dekadKey(ui.month, ui.part) : null;
  return { city: ui.city, month: ui.month, part: ui.part, bucket: ui.bucket, dekad };
}

// All three words said — the day can be read.
const draftComplete = () => !!ui.city && ui.month != null && ui.part != null && !!ui.bucket;

export function onDraftChange(fn: () => void): void {
  ui.onDraft.push(fn);
}

function draftChanged() {
  $<HTMLInputElement>('sd-where').value = ui.city ? `${ui.city.name}, ${ui.city.country}` : '';
  $<HTMLSelectElement>('sd-month').value = ui.month == null ? '' : String(ui.month);
  $<HTMLSelectElement>('sd-part').value = ui.part == null ? '' : String(ui.part);
  $<HTMLSelectElement>('sd-bucket').value = ui.bucket || '';
  syncSeeder();
  for (const fn of ui.onDraft) fn();
}

export function setDraftCity(s: DayPlace): void {
  ui.city = { id: s.id, name: s.name, country: s.country, placeSlug: s.placeSlug };
  draftChanged();
}

export function setDraftWhen(month: number | null, part: number | null): void {
  ui.month = month || null;
  ui.part = part || null;
  draftChanged();
}

export function setDraftBucket(bk: string | null): void {
  ui.bucket = bk;
  draftChanged();
}

// Where the landing and the editor say what happened — a photo read, a place
// with no readings. Both lines, because either may be the one on screen.
function setStatus(text: string, html = false) {
  const note = $('sd-note');
  if (html) note.innerHTML = text;
  else note.textContent = text;
  const dy = $('dy-status');
  dy.textContent = html ? note.textContent || '' : text;
  dy.classList.add('said');
}

// ── init ──────────────────────────────────────────────────────────────────────

export function initMyDay(context: MyDayContext): void {
  ctx = context;

  const monthSel = $<HTMLSelectElement>('sd-month');
  // Each field starts on an empty choice, so nothing is picked for the reader.
  monthSel.append(new Option('Month…', ''));
  MONTH_NAMES.forEach((m, i) => monthSel.append(new Option(m, String(i + 1))));
  const partSel = $<HTMLSelectElement>('sd-part');
  partSel.append(new Option('Part…', ''));
  PART_LABELS.forEach((p, i) => partSel.append(new Option(p, String(i + 1))));
  const bucketSel = $<HTMLSelectElement>('sd-bucket');
  bucketSel.append(new Option('Time of day…', ''));
  for (const b of state.manifest!.buckets) bucketSel.append(new Option(`${b.emoji || ''} ${b.label}`, b.key));

  monthSel.addEventListener('change', () => setDraftWhen(Number(monthSel.value), ui.part));
  partSel.addEventListener('change', () => setDraftWhen(ui.month, Number(partSel.value)));
  bucketSel.addEventListener('change', () => setDraftBucket(bucketSel.value || null));

  ctx.wireAutocomplete($<HTMLInputElement>('sd-where'), $('sd-sugg'), (s) => setDraftCity(s), { clearOnPick: false });

  $('sd-photo-btn').addEventListener('click', () => $('sd-photo-input').click());
  $('sd-photo-input').addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) handlePhoto(input.files[0]);
    input.value = ''; // the same file again is a fresh request
  });
  // Drop a photo on the editor or anywhere on the landing — the landing's
  // "Or drop a holiday photo" means the whole screen, not a target to find.
  for (const id of ['custom-editor', 'landing']) {
    const el = $(id);
    el.addEventListener('dragover', (e) => { e.preventDefault(); el.classList.add('dragover'); });
    el.addEventListener('dragleave', (e) => { if (e.target === el) el.classList.remove('dragover'); });
    el.addEventListener('drop', (e) => {
      e.preventDefault();
      el.classList.remove('dragover');
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handlePhoto(file);
    });
  }

  $('sd-apply').addEventListener('click', () => applyDay());

  // The panel's controls.
  $('peaks-close').addEventListener('click', () => setPeaksOpen(false));
  $('pk-edit').addEventListener('click', (e) => { e.stopPropagation(); store.set({ menu: 'lens-custom' }); });
  $('pk-copy').addEventListener('click', () => copyLink($('pk-copy')));
  $('pk-compare').addEventListener('click', () => ctx!.openCompare(topThree()));
  // A row's "+ Compare" reflects the comparison being gathered, wherever it
  // was changed from — this list, a briefing, the tray.
  store.subscribe(['compare'], rerenderPeaks);
  // The list answers for the date on the ruler, as the map does — scrubbing the
  // year re-ranks it (debounced, so a drag costs one request).
  store.subscribe(['dateIdx'], () => refreshPeaks());

  seedDraft();
  renderSavedDays();
  syncSeeder();
}

// The draft a fresh landing shows. A reader who has used a day before gets
// their own last day back. Anyone else gets a SUGGESTION — a well-known holiday
// place near them that is pleasant around today (/api/suggest) — said as one,
// so it reads as an invitation rather than as someone else's holiday. Until it
// lands, or if it cannot, the words stay blank: where, when, what time.
function seedDraft() {
  const last = loadSavedDays()[0];
  const hasBucket = (k: string) => state.manifest!.buckets.some((b) => b.key === k);
  // The last place they looked at — a briefing or a guide — when that is more
  // recent than the last day they authored: it opens at that place's best time
  // for their own rating (or for being outside, before they have one).
  const seen = lastPlace();
  if (seen && hasBucket(seen.bucket) && seen.at > ((last && last.at) || 0)) {
    ui.city = { id: seen.id, name: seen.name, country: seen.country, placeSlug: seen.placeSlug };
    ui.month = Number(seen.dekad.slice(0, 2));
    ui.part = Number(seen.dekad.slice(3));
    ui.bucket = seen.bucket;
    draftChanged();
    $('dy-status').textContent = seen.mine
      ? `— the last place you looked at, when it is most your kind of day. Change any word to make it yours.`
      : `— the last place you looked at, at its best for being outside. Change any word to make it yours.`;
    return;
  }
  if (last && hasBucket(last.bucket)) {
    ui.city = { id: last.id, name: last.name, country: last.country, placeSlug: last.placeSlug };
    ui.month = Number(last.dekad.slice(0, 2));
    ui.part = Number(last.dekad.slice(3));
    ui.bucket = last.bucket;
    draftChanged();
    return;
  }
  draftChanged();
  const { lat, lon } = visitorLocation();
  const now = new Date();
  const md = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  fetch(`/api/suggest?lat=${lat.toFixed(1)}&lon=${lon.toFixed(1)}&md=${md}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((s: (CityRow & { dekad: string; bucket: string }) | null) => {
      // The reader may have started filling it in while this was in flight.
      if (!s || ui.city || ui.month != null || ui.bucket) return;
      ui.city = { id: s.id, name: s.name, country: s.country, placeSlug: s.placeSlug };
      ui.month = Number(s.dekad.slice(0, 2));
      ui.part = Number(s.dekad.slice(3));
      ui.bucket = s.bucket;
      draftChanged();
      const dy = $('dy-status');
      dy.textContent = '— that day is only a suggestion: pleasant around now, not far from you. Change any word to make it yours.';
    })
    .catch(() => { /* the blank sentence still works */ });
}

function syncSeeder() {
  $<HTMLButtonElement>('sd-apply').disabled = !draftComplete();
  const go = document.getElementById('dy-go') as HTMLButtonElement | null;
  if (go) go.disabled = !draftComplete();
  const chip = $('sd-photo-loc');
  chip.hidden = !(ui.photo && ui.photo.cityRow && (!ui.city || ui.city.id !== ui.photo.cityRow.id));
}

// ── applying a day ──────────────────────────────────────────────────────────────

// Fetch the day's typical conditions and restate them as the recipe, then go
// to the answer: the map, painting the rating, with "Your day" open beside it.
//
// `fromRoute` is a /day/… link being opened: the same steps, but the camera
// jumps rather than flies (there is no previous view to fly from) and the
// event is not counted as a reader authoring a day.
export async function applyDay({ fromRoute = false }: { fromRoute?: boolean } = {}): Promise<boolean> {
  if (!ui.city || !ctx || !draftComplete()) return false;
  if (!fromRoute) track('recipe_apply');
  const token = ++ui.seedToken;
  const buttons = [$<HTMLButtonElement>('sd-apply'), $<HTMLButtonElement>('dy-go')];
  const labels = buttons.map((b) => b.textContent);
  for (const b of buttons) { b.disabled = true; b.textContent = 'Reading…'; }

  let data: ConditionsResponse | null = null;
  let status = 0;
  try {
    const res = await fetch(`/api/conditions?city=${ui.city.id}&md=${mdParam()}&bucket=${ui.bucket!}&v=${state.version || ''}`);
    status = res.status;
    if (res.ok) data = await res.json();
  } catch {
    /* handled below */
  }
  if (token !== ui.seedToken) return false;
  buttons.forEach((b, i) => { b.textContent = labels[i]; });
  syncSeeder();

  if (!data) {
    // Distinguish the transient from the permanent: a 4xx will never fix itself.
    setStatus(
      status === 422
        ? 'No usable readings there at that time of year — try a nearby, larger place.'
        : status === 503
          ? 'The ratings index isn’t built on this server yet.'
          : status >= 400 && status < 500
            ? `Couldn’t read that day — the request was rejected (${status}). Try a different place or date.`
            : 'Couldn’t reach the server just now — try again in a moment.',
    );
    if (status) console.warn(`[myday] /api/conditions -> ${status}`);
    return false;
  }

  const recipe = recipeFromConditions(data.conditions);
  if (!recipe) {
    setStatus('No feels-like reading there at that time of year — try another place.');
    return false;
  }

  ctx.pause(); // arriving somewhere deliberately is not a moment to keep playing
  ctx.unlockCustom();
  // The time of day first — the rating is about THAT hour — then the dekad, so
  // the map lands on the day itself rather than scrubbing to it a frame later.
  ctx.setBucket(data.bucket);
  const idx = (ui.month! - 1) * DEKADS_PER_MONTH + (ui.part! - 1);
  if (idx >= 0 && idx < state.dates.length) ctx.showFrame(idx);
  ctx.setRecipe(recipe);
  ctx.syncCustomEditor();
  // The seed goes in BEFORE the lens switches, so the sentence's first render
  // of the custom rating already reads "days like Split".
  const seed: DaySeed = {
    id: data.id,
    name: data.name,
    country: data.country,
    placeSlug: data.placeSlug || ui.city.placeSlug || '',
    lat: data.lat,
    lon: data.lon,
    dekad: data.dekad,
    bucket: data.bucket,
    canon: state.recipeCanon,
    conditions: { ...data.conditions },
  };
  store.set({ daySeed: seed, menu: null });
  ctx.applyVariable('custom');
  saveDay(data);
  setStatus(`Set from <b>${escapeHtml(dayTitle(seed))}</b> — ${escapeHtml(conditionWords(data.conditions))}`, true);

  store.set({ peaksOpen: true });
  ui.activeId = data.id;
  ctx.showMap([data.lon, data.lat], DAY_ZOOM, !fromRoute);
  refreshPeaks({ immediate: true });
  ctx.persist();
  return true;
}

// A /day/{place}/{date}/{bucket}/ link: set the draft from it and apply it.
export async function applyDayFromRoute(place: CityHit, dateKey: string | null, bucket: string | null): Promise<boolean> {
  ui.city = { id: place.id, name: place.name, country: place.country, placeSlug: place.placeSlug };
  if (dateKey) {
    ui.month = Number(dateKey.slice(0, 2));
    ui.part = Number(dateKey.slice(3));
  }
  if (bucket && state.manifest!.buckets.some((b) => b.key === bucket)) ui.bucket = bucket;
  draftChanged();
  return applyDay({ fromRoute: true });
}

// "Split · late June · midday".
export function dayTitle(seed: DaySeed): string {
  return `${seed.name} · ${lower1(sectionLabel(seed.dekad))} · ${bucketWord(seed.bucket)}`;
}

// ── photo quick-fill ────────────────────────────────────────────────────────────
// A holiday photo says where and when in one drop; EXIF is read on-device and
// the file never leaves the browser. Whatever it cannot tell us stays as the
// reader left it — the words are the real interface, this only fills them.

export function pickPhoto(): void {
  $('sd-photo-input').click();
}

export async function handlePhoto(file: File): Promise<void> {
  const thumb = $<HTMLImageElement>('sd-thumb');
  if (ui.photo && ui.photo.url) URL.revokeObjectURL(ui.photo.url);
  const url = URL.createObjectURL(file);
  thumb.src = url;
  thumb.hidden = false;
  ui.photo = { url, cityRow: null };
  setStatus('Reading the photo…');

  let x: ExifResult;
  try {
    x = parseExif(await file.slice(0, EXIF_SCAN_BYTES).arrayBuffer());
  } catch {
    x = { ok: false, reason: 'not-jpeg', lat: null, lon: null, date: null, hour: null };
  }

  if (x.date) {
    ui.month = x.date.m;
    ui.part = x.date.d <= 10 ? 1 : x.date.d <= 20 ? 2 : 3;
    if (x.hour != null) {
      // A camera's wall clock skews from local solar by ≤ ~1.5 h — inside a
      // bucket's 3 h window, so the nearest targetHour is the right bucket.
      let best = ui.bucket;
      let bestD = Infinity;
      for (const b of state.manifest!.buckets) {
        const gap = Math.abs(x.hour - b.targetHour);
        const d = Math.min(gap, 24 - gap);
        if (d < bestD) { bestD = d; best = b.key; }
      }
      ui.bucket = best;
    }
  }

  let near: NearResponse | null = null;
  if (x.lat != null && x.lon != null) {
    try {
      const res = await fetch(`/api/near?lat=${x.lat.toFixed(4)}&lon=${x.lon.toFixed(4)}`);
      if (res.ok) near = await res.json();
    } catch {
      /* offline — the words still work by hand */
    }
    if (near) {
      ui.photo!.cityRow = near;
      ui.city = { id: near.id, name: near.name, country: near.country, placeSlug: near.placeSlug };
    }
  }
  draftChanged();

  const when = x.date ? `${x.date.d} ${MONTH_NAMES[x.date.m - 1]} ${x.date.y}` : null;
  if (near && when) setStatus(`📍 near ${near.name}, ${near.country} · ${when} — find days like it.`);
  else if (near) setStatus(`📍 near ${near.name}, ${near.country} — when was it?`);
  else if (when) setStatus(`Taken ${when} — where was it?`);
  else if (!x.ok && x.reason === 'not-jpeg') setStatus('Couldn’t read this photo (iPhone HEIC isn’t supported) — say the place and date instead.');
  else setStatus('No place or date in this photo — apps often strip them. Say them instead.');
}

// The photo's thumbnail belongs on the panel's seed card only while the seed
// is the day the photo named.
function photoFor(seed: DaySeed | null): string | null {
  return seed && ui.photo && ui.photo.cityRow && ui.photo.cityRow.id === seed.id ? ui.photo.url : null;
}

// ── days you have used before ────────────────────────────────────────────────────

function loadSavedDays(): SavedDay[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(DAYS_KEY) || '[]');
    // Storage is only ever written by saveDay below, so an array of rows is
    // taken at its word — the same trust the map's own persistence extends.
    return Array.isArray(list) ? (list as SavedDay[]) : [];
  } catch {
    return [];
  }
}

function saveDay(data: ConditionsResponse) {
  try {
    const entry: SavedDay = { id: data.id, name: data.name, country: data.country, dekad: data.dekad, bucket: data.bucket, placeSlug: data.placeSlug, at: Date.now() };
    const list = [entry, ...loadSavedDays().filter((h) => !(h.id === entry.id && h.dekad === entry.dekad))].slice(0, MAX_SAVED);
    localStorage.setItem(DAYS_KEY, JSON.stringify(list));
  } catch {
    /* best-effort, like the map's own persistence */
  }
  renderSavedDays();
}

function renderSavedDays() {
  const box = $('sd-saved');
  if (!box) return;
  const list = loadSavedDays();
  box.hidden = !list.length;
  box.replaceChildren();
  for (const h of list) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sd-chip';
    b.textContent = `${h.name} · ${lower1(sectionLabel(h.dekad))}`;
    b.addEventListener('click', () => {
      ui.city = { id: h.id, name: h.name, country: h.country, placeSlug: h.placeSlug };
      ui.month = Number(h.dekad.slice(0, 2));
      ui.part = Number(h.dekad.slice(3));
      if (state.manifest!.buckets.some((bk) => bk.key === h.bucket)) ui.bucket = h.bucket;
      draftChanged();
      applyDay(); // a saved day is a complete answer — use it
    });
    box.appendChild(b);
  }
}

// ── the panel ────────────────────────────────────────────────────────────────────

// Only the custom rating has a "Your day": it is the one rating a reader made,
// and the panel is where the made thing is answered. Beside the map AND over an
// open briefing — picking a row opens it, and the list has to survive that or
// you cannot walk down it. Never on the landing or a comparison.
function railVisible() {
  const v = state.variable as VarKey;
  return state.peaksOpen && (state.view === 'map' || state.view === 'city') && !!(VAR[v] && VAR[v].custom);
}

export function setPeaksOpen(open: boolean): void {
  if (!ctx) return;
  store.set({ peaksOpen: open }); // through the store: the corner word listens
  ctx.persist();
  refreshPeaks({ immediate: true });
}

// Whether the panel is up — the corner word reads it for its active state.
export function peaksShowing(): boolean {
  return !!ctx && railVisible();
}

export function syncPeaksChrome(): void {
  const panel = $('peaks');
  if (!panel || !ctx) return;
  const on = railVisible();
  panel.hidden = !on;
  document.body.classList.toggle('peaks-open', on);
  if (state.view !== 'city') panel.style.transform = '';
}

export function refreshPeaks({ immediate = false }: { immediate?: boolean } = {}): void {
  syncPeaksChrome();
  if (!ctx || !railVisible()) return;
  renderHead();
  if (ui.timer) clearTimeout(ui.timer);
  ui.timer = setTimeout(() => {
    ui.timer = null;
    runPeaks();
  }, immediate ? 0 : PEAK_DEBOUNCE_MS);
}

// The part of the panel that is about the day rather than the ranking.
function renderHead() {
  const seed = state.daySeed;
  const card = $('pk-seed');
  card.hidden = !seed;
  if (seed) {
    $('pk-seed-title').textContent = dayTitle(seed);
    $('pk-seed-sub').textContent = conditionWords(seed.conditions, { wind: true });
    const thumb = $<HTMLImageElement>('pk-thumb');
    const url = photoFor(seed);
    thumb.hidden = !url;
    if (url) thumb.src = url;
  }
  // The list answers for the date on the ruler, so the caption names it.
  const when = state.dates.length ? lower1(sectionLabel(state.dates[state.dateIdx])) : '';
  $('peaks-sub').textContent = seed
    ? `The places most like your day in ${when}, the best in each region — and how long it lasts there. Move the year to ask about another time.`
    : `The best place in each region for your rating in ${when}. Move the year to ask about another time.`;
  // The address, as the reader would paste it.
  const path = seed && seed.placeSlug ? dayPath({ place: seed.placeSlug, dateKey: seed.dekad, bucket: seed.bucket }).replace(/\/$/, '') : location.pathname;
  $('pk-path').textContent = path;
}

async function runPeaks() {
  const token = ++ui.peakToken;
  const at = state.dates[state.dateIdx] || '';
  const key = `${state.recipeCanon}|${state.bucket}|${at}`;
  const q = `/api/best?mode=custom&bucket=${state.bucket}&perRegion=${PER_REGION}&limit=${PER_REGION * 11}&at=${at}&r=${state.recipeCanon}&v=${state.version || ''}`;
  const list = $('peaks-list');
  // Keep the current list up while re-ranking the same day (a slider drag must
  // not strobe); a different time of day is a different list.
  if (ui.renderedKey && ui.renderedKey.split('|')[1] !== state.bucket) ui.peaks = [];
  ui.renderedKey = key;
  if (!ui.peaks.length) list.innerHTML = '<div class="pk-note">Ranking…</div>';

  let data: BestResponse | null = null;
  try {
    const res = await fetch(q);
    if (res.ok) data = await res.json();
  } catch {
    /* handled below */
  }
  if (token !== ui.peakToken) return;
  if (!data) {
    list.innerHTML = '<div class="pk-note">Couldn’t rank right now.</div>';
    return;
  }
  ui.peaks = data.results;
  renderPeaks();
}

// A day's conditions, read as words rather than as coloured dots.
//
//   feels 27°, 12% rain, 15% cloud, a light breeze   (the sentence's helper)
//   27° · 12% rain · 15% cloud · 3.1 m/s              (the seed card)
export function conditionWords(c: Conditions | Record<string, number | null | undefined>, { wind = false, prose = false }: { wind?: boolean; prose?: boolean } = {}): string {
  const out: string[] = [];
  if (c.app != null) out.push(prose ? `feels ${Math.round(c.app)}°` : `${Math.round(c.app)}°`);
  if (c.rain != null) out.push(`${Math.round(c.rain)}% rain`);
  if (c.sky != null) out.push(`${Math.round(c.sky)}% cloud`);
  if (c.ws != null && prose) out.push(breeze(c.ws));
  else if (c.ws != null && wind) out.push(`${c.ws.toFixed(1)} m/s`);
  return out.join(prose ? ', ' : ' · ');
}

// Wind as it is felt, on the Beaufort names.
function breeze(ws: number): string {
  return ws < 1.6 ? 'still air' : ws < 3.4 ? 'a light breeze' : ws < 5.5 ? 'a gentle breeze' : ws < 8 ? 'a steady wind' : 'a strong wind';
}

export function rerenderPeaks(): void {
  if (ui.peaks.length) renderPeaks();
}

// Regions in the order their best place ranks — the list still reads best first.
// A day's own region wins a tie: a recipe read off a real day scores the best
// place in most regions at the ceiling, and among equals the reader's own part
// of the world is the one to read first.
function groupPeaks(peaks: Peak[]): { region: string; rows: Peak[] }[] {
  const seedRegion = state.daySeed ? peaks.find((p) => p.id === state.daySeed!.id)?.region ?? seedRegionGuess() : null;
  const groups = new Map<string, Peak[]>();
  for (const p of peaks) {
    const r = p.region || '';
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r)!.push(p);
  }
  return [...groups.entries()]
    .map(([region, rows]) => ({ region, rows: rows.sort((a, b) => b.score - a.score) }))
    .sort((a, b) => b.rows[0].score - a.rows[0].score || Number(b.region === seedRegion) - Number(a.region === seedRegion));
}

// The seed's region when the seed itself is not in the list: the region of the
// nearest listed place is close enough to name "the reader's part of the world".
function seedRegionGuess(): string | null {
  const s = state.daySeed;
  if (!s || !ui.peaks.length) return null;
  let best: Peak | null = null;
  let bestD = Infinity;
  for (const p of ui.peaks) {
    const d = (p.lat - s.lat) ** 2 + ((p.lon - s.lon) * Math.cos((s.lat * Math.PI) / 180)) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best ? best.region ?? null : null;
}

// The first three rows as the panel shows them.
function topThree(): ComparePlace[] {
  return groupPeaks(ui.peaks).map((g) => g.rows[0]).slice(0, 3).map(placeOf);
}

const placeOf = (p: CityRow): ComparePlace => ({ id: p.id, name: p.name, country: p.country, placeSlug: p.placeSlug || '', lat: p.lat, lon: p.lon });

function renderPeaks() {
  const list = $('peaks-list');
  list.replaceChildren();
  renderFoot();
  if (!ui.peaks.length) {
    list.innerHTML = '<div class="pk-note">Nowhere on Earth rates Good for this. Widen the ideal range or ease the weightings.</div>';
    return;
  }
  const seedId = state.daySeed ? state.daySeed.id : null;
  for (const g of groupPeaks(ui.peaks)) {
    const head = document.createElement('div');
    head.className = 'pk-region';
    head.textContent = REGION_LABEL[g.region as RegionKey] || 'Elsewhere';
    list.appendChild(head);
    const open = ui.expanded.has(g.region);
    g.rows.forEach((r, i) => {
      if (i >= SHOWN_PER_REGION && !open) return;
      list.appendChild(peakRow(r, r.id === seedId));
    });
    if (g.rows.length > SHOWN_PER_REGION) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'pk-more';
      more.textContent = open ? 'Fewer' : `${g.rows.length - SHOWN_PER_REGION} more in ${REGION_LABEL[g.region as RegionKey] || 'this region'}`;
      more.addEventListener('click', () => {
        if (open) ui.expanded.delete(g.region);
        else ui.expanded.add(g.region);
        renderPeaks();
      });
      list.appendChild(more);
    }
  }
  syncActiveRow();
}

function peakRow(r: Peak, isSeed: boolean): HTMLElement {
  const row = document.createElement('div');
  row.className = `pk-row${isSeed ? ' seed' : ''}`;
  row.dataset.id = String(r.id);
  const when =
    r.span[0] !== r.span[1]
      ? `${sectionLabelShort(r.span[0])} – ${sectionLabelShort(r.span[1])}`
      : lower1(sectionLabel(r.dekad));
  const band = r.score >= 80 ? 'ideal' : 'good';
  const added = state.compare.places.some((p) => p.id === r.id);
  // No rank numeral and no coloured condition dots: a place, when it is like
  // your day there, and how like — three facts in the order a sentence puts them.
  // Then the one action a row has besides opening: put it in the comparison.
  // Always there rather than behind a mode, so it is found by looking.
  row.innerHTML =
    `<button type="button" class="pk-main">` +
    `<span class="pk-body">` +
    `<span class="pk-place">${escapeHtml(r.name)}<span class="pk-country">${escapeHtml(r.country)}</span></span>` +
    `<span class="pk-when">${when}</span>` +
    `</span>` +
    `<span class="pk-score ${band}">${r.score}</span>` +
    `</button>` +
    `<button type="button" class="pk-add${added ? ' on' : ''}" aria-pressed="${added}" title="${added ? 'Take out of the comparison' : 'Add to the comparison'}">${added ? '✓ Comparing' : '+ Compare'}</button>`;
  row.querySelector('.pk-add')!.addEventListener('click', (e) => {
    e.stopPropagation();
    ctx!.toggleCompare(placeOf(r));
  });
  row.querySelector('.pk-main')!.addEventListener('click', () => {
    // Picking a match opens it — the year moves to its best dekad and the
    // briefing comes up. The list stays beside it, so the next candidate is one
    // click away rather than a journey back.
    ui.activeId = r.id;
    ctx!.pause();
    const idx = state.dates.indexOf(r.dekad);
    if (idx >= 0) ctx!.showFrame(idx);
    ctx!.openCity(r.id, { name: r.name, nameAscii: r.nameAscii, country: r.country, pop: r.pop, lon: r.lon, lat: r.lat, placeSlug: r.placeSlug });
    syncActiveRow();
  });
  return row;
}

// The footer: the address and its copy button, and the quick bridge into
// Compare — the top three as the list shows them.
function renderFoot() {
  const btn = $<HTMLButtonElement>('pk-compare');
  btn.disabled = ui.peaks.length < 2;
  btn.textContent = ui.peaks.length >= 3 ? 'Compare the top three →' : 'Compare the top two →';
}

function syncActiveRow() {
  document.querySelectorAll<HTMLElement>('#peaks-list .pk-row').forEach((el) => {
    el.classList.toggle('active', Number(el.dataset.id) === ui.activeId);
  });
}

// Copy the page's own address — whatever view is showing, it is the link.
export function copyLink(btn: HTMLElement): void {
  const url = location.origin + location.pathname + location.search;
  const done = (ok: boolean) => {
    const was = btn.dataset.label || btn.textContent || '';
    btn.dataset.label = was;
    btn.textContent = ok ? 'Copied' : 'Copy failed';
    setTimeout(() => { btn.textContent = was; }, 1400);
  };
  try {
    navigator.clipboard.writeText(url).then(() => done(true), () => done(false));
  } catch {
    done(false);
  }
}
